"use client";

import { ReactElement, useEffect, useState } from "react";

import { fetchJobs } from "@/app/[locale]/jobs/action";
import Button from "@/components/atoms/Button";
import { Loader } from "@/components/atoms/Loader";
import JobListCta from "@/components/blocks/JobListCta";
import JobsChat from "@/components/blocks/JobsChat";
import { JobCard } from "@/components/molecules/JobCard";
import { SDK } from "@/graphql/sdk";
import { Job } from "@/graphql/sdk/__generated__";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

type Variables<TFn extends (...args) => unknown> = Parameters<TFn>[0];

interface LoadMoreJobsProps {
  i18n: {
    applied_on: string;
    internal: string;
    load_more: string;
    no_results: string;
    total_results: string;
    urgent: string;
    viewed: string;
  };
  initialJobs: Job[] | null;
  locale: AllowedLanguages;
  totalJobs: number;
  variables: Variables<typeof SDK.searchJobs>;
}

export const LoadMoreJobs = ({
  i18n,
  initialJobs,
  locale,
  totalJobs,
  variables,
}: LoadMoreJobsProps) => {
  const [jobs, setJobs] = useState<Job[] | null>(initialJobs);
  const [page, setPage] = useState<number>(1);
  const [loadMore, setLoadMore] = useState<boolean>(false);
  const [isReachedEnd, setIsReachedEnd] = useState<boolean>(false);
  const { t } = useTranslation(locale);

  function loadMoreJobs() {
    const next = page + 1;

    fetchJobs({ variables: variables, page: next })
      .then(({ jobsSearch }) => {
        if (jobsSearch?.jobs?.length) {
          setPage(next);
          setJobs((prev: Job[] | null) => [
            ...(prev?.length ? prev : []),
            ...jobsSearch.jobs,
          ]);
        }

        if (!jobsSearch || jobsSearch.to >= jobsSearch.total_results) {
          setIsReachedEnd(true);
        }

        setLoadMore(false);
      })
      .catch((error) => {
        console.error(`Error fetching jobs: ${error}`);
      });
  }

  useEffect(() => {
    if (loadMore) {
      loadMoreJobs();
    }
  }, [loadMore]);

  const componentsArray: ReactElement[] | null =
    jobs?.map((job) => <JobCard job={job} locale={locale} key={job.id} />) ??
    [];

  const insertAtIndexOrPush = (index: number, component: ReactElement) => {
    if (componentsArray && componentsArray?.length > index) {
      componentsArray.splice(index, 0, component);
    } else {
      componentsArray?.push(component);
    }
  };

  const getValuesForChat = () => {
    const paramsForChat = { ...variables } ?? {};
    delete paramsForChat.lat;
    delete paramsForChat.lng;
    // Delete non-translatable values, we'll add them back later
    delete paramsForChat.maxDistance;
    delete paramsForChat.keyword;
    delete paramsForChat.city;
    let values = Object.values(paramsForChat).flat();
    values = values.filter((n) => n);

    // Translate
    values = values.map((value) => t(`filter.option.${value}`));

    // Add non-translatable values
    values.push(variables?.keyword ?? null);
    values.push(variables?.city ?? null);

    values = values.filter((n) => n);

    return values;
  };

  // Insert specific blocks (call to actions) in the jobs list
  if (jobs && jobs.length > 0) {
    insertAtIndexOrPush(2, <JobListCta locale={locale} key={"jobs-cta"} />);
  }
  insertAtIndexOrPush(
    21,
    <JobsChat
      locale={locale}
      key={"jobs-chat"}
      params={getValuesForChat() ?? []}
    />,
  );

  return (
    <>
      <div className="mb-6 lg:mb-24">
        {jobs?.length > 0 ? (
          <>
            <div className="mb-4 text-black-300">
              {totalJobs} {i18n.total_results}
            </div>
            <div className="flex flex-col gap-3 lg:gap-8">
              {componentsArray}
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-8 lg:gap-16">
            <div
              className="prose text-xl text-gray-700"
              dangerouslySetInnerHTML={{ __html: i18n.no_results }}
            ></div>
            {componentsArray}
          </div>
        )}
      </div>

      {!isReachedEnd && jobs && jobs?.length < totalJobs && (
        <>
          {loadMore ? (
            <Loader locale={locale} size="medium" />
          ) : (
            <div className="flex items-center gap-6 lg:gap-14">
              <hr className="w-full shrink" />
              <Button
                className="btn btn-white shrink-0 border-border"
                type="button"
                arrow={false}
                onClick={() => setLoadMore(true)}
              >
                {i18n.load_more}
              </Button>
              <hr className="w-full shrink" />
            </div>
          )}
        </>
      )}
    </>
  );
};
